// src/products/analysis/pages/EnergyMarketsPage.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  Flame,
  Loader2,
  RefreshCw,
  TrendingDown,
  TrendingUp
} from 'lucide-react';
import { getCommodityQuote, getEnergyMarkets } from '../services/commodityService';

const EnergyMarketsPage = () => {
  const energyInstruments = useMemo(() => getEnergyMarkets(), []);
  const [markets, setMarkets] = useState([]);
  const [loadingStates, setLoadingStates] = useState({});
  const [pageError, setPageError] = useState(null);

  const seedMarkets = useCallback(() => {
    setMarkets(
      energyInstruments.map((instrument) => ({
        ...instrument,
        currentPrice: null,
        priceChange: null,
        priceChangePercent: null,
        date: null,
        error: null
      }))
    );
  }, [energyInstruments]);

  const loadEnergyPrices = useCallback(async (instruments) => {
    setPageError(null);

    for (let index = 0; index < instruments.length; index += 1) {
      const instrument = instruments[index];

      if (index > 0) {
        await new Promise((resolve) => setTimeout(resolve, 120));
      }

      setLoadingStates((prev) => ({ ...prev, [instrument.symbol]: true }));

      try {
        const quote = await getCommodityQuote(instrument.symbol);

        setMarkets((prev) =>
          prev.map((item) =>
            item.symbol === instrument.symbol ? { ...item, ...quote, error: null } : item
          )
        );
      } catch (error) {
        console.warn(`Energy market fetch failed for ${instrument.symbol}:`, error.message);

        setMarkets((prev) =>
          prev.map((item) =>
            item.symbol === instrument.symbol
              ? { ...item, error: error.message, currentPrice: null }
              : item
          )
        );

        setPageError((prev) =>
          prev ?? 'Some energy markets failed to load. Please refresh or try again later.'
        );
      } finally {
        setLoadingStates((prev) => ({ ...prev, [instrument.symbol]: false }));
      }
    }
  }, []);

  const refreshPrices = useCallback(async () => {
    seedMarkets();
    await loadEnergyPrices(energyInstruments);
  }, [energyInstruments, loadEnergyPrices, seedMarkets]);

  useEffect(() => {
    // Seed UI with instrument metadata immediately
    seedMarkets();
    loadEnergyPrices(energyInstruments);
  }, [energyInstruments, loadEnergyPrices, seedMarkets]);

  const formatPrice = (price, unit) => {
    if (price === null || price === undefined) {
      return 'N/A';
    }

    const localeOptions = price >= 1000
      ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      : { minimumFractionDigits: 2, maximumFractionDigits: 4 };

    return `${unit} ${price.toLocaleString('en-US', localeOptions)}`;
  };

  const formatChange = (changePercent, changeValue) => {
    if (changePercent === null || changePercent === undefined) {
      if (changeValue === null || changeValue === undefined) {
        return 'N/A';
      }

      return `${changeValue >= 0 ? '+' : ''}${changeValue.toFixed(2)}`;
    }

    const prefix = changePercent >= 0 ? '+' : '';
    return `${prefix}${changePercent.toFixed(2)}%`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Energy Markets</h1>
          <p className="mt-1 text-sm text-gray-500">
            Key global energy benchmarks updated from Yahoo Finance futures data.
          </p>
        </div>
        <button
          onClick={refreshPrices}
          disabled={Object.values(loadingStates).some(Boolean)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {Object.values(loadingStates).some(Boolean) ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4" />
          )}
          Refresh Prices
        </button>
      </div>

      {pageError && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-2 text-yellow-800">
          <AlertCircle className="w-5 h-5" />
          <div className="flex-1 text-sm">
            <p className="font-medium">{pageError}</p>
            <p className="text-xs mt-1">
              Prices are sourced via Yahoo Finance&apos;s energy futures endpoints. Energy contracts may
              occasionally pause trading or return delayed quotes.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {markets.map((market) => {
          const isLoading = loadingStates[market.symbol];
          const isPositive = Number(market.priceChangePercent) >= 0;

          return (
            <div
              key={market.symbol}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{market.name}</h2>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">{market.symbol}</p>
                </div>
                <Flame className="w-5 h-5 text-orange-500" />
              </div>

              <div className="space-y-3">
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1">Current Price</p>
                  {isLoading ? (
                    <div className="flex items-center gap-2 text-gray-400">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span className="text-sm">Loading...</span>
                    </div>
                  ) : market.error ? (
                    <p className="text-sm text-red-500">{market.error}</p>
                  ) : market.currentPrice !== null ? (
                    <p className="text-2xl font-bold text-gray-900">
                      {formatPrice(market.currentPrice, market.unit)}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-400">Waiting to load...</p>
                  )}
                </div>

                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1">Latest Session Change</p>
                  {isLoading ? (
                    <p className="text-sm text-gray-400">Loading...</p>
                  ) : market.error ? (
                    <p className="text-xs text-red-500">{market.error}</p>
                  ) : market.priceChangePercent !== null && market.priceChangePercent !== undefined ? (
                    <div className="flex items-center gap-2">
                      {isPositive ? (
                        <TrendingUp className="w-5 h-5 text-green-500" />
                      ) : (
                        <TrendingDown className="w-5 h-5 text-red-500" />
                      )}
                      <p
                        className={`text-lg font-semibold ${
                          isPositive ? 'text-green-600' : 'text-red-600'
                        }`}
                      >
                        {formatChange(market.priceChangePercent, market.priceChange)}
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">N/A</p>
                  )}
                </div>

                {market.date && (
                  <p className="text-xs text-gray-500 border-t border-gray-100 pt-3">
                    Last updated {new Date(market.date).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {markets.length === 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 flex items-center gap-3 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin" />
          <p className="text-sm">Preparing energy market data…</p>
        </div>
      )}

      {markets.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-2 text-blue-800">
          <AlertCircle className="w-5 h-5 mt-0.5" />
          <div className="text-xs">
            Quotes reflect front-month futures contracts (WTI, Brent, Natural Gas). Prices can be delayed up
            to 10 minutes depending on the exchange.
          </div>
        </div>
      )}
    </div>
  );
};

export default EnergyMarketsPage;


